<template>
    <div class="script-question">
        <base-field
            :class="[
                'script-question__block',
                { 'background--grey--light' : $route.meta.isAdd }
            ]"
            :settings="{
                label: { class: generateLabelClass, text: $t('global.question') },
                input: {
                    component: 'Textarea',
                    id: `question[${questionId}]`,
                    name: `question[${questionId}]`,
                    class: 'script-question__input typography--default color--default',
                    disableClass: 'field__textarea--disable',
                    value: values.question,
                    placeholder: $t('global.writeYourQuestionHere'),
                    isMissing: isMissing
                },
                alert: {
                    class: 'textarea'
                },
                isEdition,
                reset,
                allowFilling: allowFilling
            }"
        />
        <base-field
            :settings="{
                label: { class: generateLabelClass, text: $t('global.questionType') },
                input: {
                    component: 'InputAutocomplete',
                    id: `type[${questionId}]`,
                    name: `type[${questionId}]`,
                    type: 'text',
                    value: values.type.id,
                    targetClass: 'field__input--default typography--default color--default',
                    disableClass: 'field__textarea--disable',
                    browserAutocomplete: 'off',
                    dynamicData: {
                        data: inputTypes,
                        apiValue: { value: values.type.id, label: $t(`inputTypes.${values.type.label}`) }
                    }
                },
                isEdition,
                reset,
                allowFilling: allowFilling
            }"
        />
        <possible-answers
            v-if="showAnswerOptions"
            v-bind="{
                questions: generateQuestions,
                questionType: payload[`type[${questionId}]`] || questions.find( question => question.id === questionId ).type.id || 1,
                questionId,
                isEdition,
                reset,
                itsChainedScript,
                apiAnswers: values.nextQuestions.length ? values.nextQuestions : [],
                generateLabelClass,
                allowFilling: allowFilling
            }"
        />
        <base-field
            v-else-if="itsChainedScript"
            :settings="{
                label: { class: generateLabelClass, text: $t('global.nextQuestion') },
                input: {
                    component: 'InputAutocomplete',
                    id: `next-question[${questionId}]`,
                    name: `next-question[${questionId}]`,
                    type: 'text',
                    value: values.nextQuestions.id,
                    targetClass: 'field__input--default typography--default color--default',
                    disableClass: 'field__textarea--disable',
                    browserAutocomplete: 'off',
                    placeholder: $t('global.nextQuestion'),
                    dynamicData: {
                        data: generateQuestions,
                        apiValue: { value: values.nextQuestions.id, label: values.nextQuestions.label }
                    }
                },
                isEdition,
                reset,
                allowFilling: allowFilling
            }"
        />
    </div>
</template>

<script>
// packages
import {
    ref, watch, computed, reactive
} from '@vue/composition-api';

// utils
import { transformToInt } from '@/utils/';

// store
import { mapState, useActions, useStore } from '@/hooks/use-store';

// configs
import { scriptInputTypes } from '@/configs/const';

// i18n
import i18n from '@/plugins/i18n';

export default {
    components: {
        PossibleAnswers: () => import( /* webpackChunkName: 'PossibleAnswers'  */ '@/components/bundles/script/blocks/PossibleAnswers' ),
        BaseField: () => import( /* webpackChunkName: 'BaseField'  */ '@/components/bundles/fields/BaseField' )
    },
    props: {
        questions: Array,
        questionId: Number,
        values: {
            type: Object,
            default: () => ({ question: '', type: {}, nextQuestions: {} })
        },
        scriptType: Number,
        isMissing: Boolean,
        isEdition: Boolean,
        allowFilling: Boolean,
        reset: Boolean,
        generateLabelClass: String
    },
    setup ( props ) {
        const showAnswerOptions = ref( [ 3, 4, 5 ].includes( props.values.type.id ) );
        const inputTypes = computed( () => scriptInputTypes
            .filter( type => type.scriptTypesAllowed.includes( props.scriptType ) )
            .map( ({ value, label }) => ({ value, label: i18n.t( `inputTypes.${label}` ) }) ) );

        // obtenção das acções
        const { actions } = useActions();

        // obtenção do payload
        const payload = mapState({ module: 'storePayload', key: 'payload' });

        // listener para validar quando deverá
        // aparecer o bloco para o utilizador introduzir
        // as possibilidades de resposta da pergunta.
        // Só deverá aparecer quando o tipo de input for
        // igual à 3 ( Escolha multípla ) ou 5 ( Dropdown ).
        // Se houver um cancelar de submissão, o type virá undefined
        // mas se o type que vem dos props for igual a 3 ou 5, tambem será
        // possivel mostrar as possibilidades de resposta da pergunta.
        watch( () => payload.value[`type[${props.questionId}]`],
            type => {
                showAnswerOptions.value = [ 3, 4, 5 ].includes( transformToInt( type ) )
                    || ( type === undefined && [ 3, 4, 5 ].includes( props.values.type.id ) );

                if ( showAnswerOptions.value )
                    actions( 'deleteField', { name: `next-question[${props.questionId}]` });
            });

        const { getters } = useStore();
        const mapedQuestions = reactive({
            questions: getters.helpers.scriptMapedQuestions,
            updated: false
        });

        /**
         * Função que adiciona nova pergunta ao array das mappedQuestions.
         * Após adicionar grava numa variável a informação de que o array foi atualizado.
         * */
        const updateQuestion = ( element, payloadValue ) => {
            mapedQuestions.questions[element] = payloadValue[element];
            mapedQuestions.updated = true;
        };

        watch( () => mapedQuestions,
            () => {
                mapedQuestions.updated = false;
            },
            {
                deep: true
            });

        watch( () => payload.value,
            payloadValue => {
                Object.keys( payloadValue ).forEach( element => {
                    if ( element.includes( 'question[' ) && !element.includes( 'next-' ) && !element.includes( '-answer' ) )
                        updateQuestion( element, payloadValue );
                });
            });

        /**
         * [computed]
         * Geração das respostas já preenchidas
         * pelo utilizador, de forma a que apareçam
         * na input de "próxima questão".
         *
         * @since 1.0.0
         * @category computed
         * @autor Tiago Martinho
         *
        */
        const generateQuestions = computed( () => {
            if ( mapedQuestions.updated )
                mapedQuestions.updated = false;

            return props.questions
                // só deve aparecer questões diferentes da que está selecionada e que tenham valor
                // helper em vez do payload, // passar do payload para o helper on regist ( com watcher no payload )
                .filter( ({ id }) => mapedQuestions.questions[`question[${id}]`] )
                // estruturação do array, de forma a que seja preenchido na drop.
                .map( ({ id, operation }, index ) => ({
                    value: index,
                    label: mapedQuestions.questions[`question[${id}]`].length < 60
                        ? mapedQuestions.questions[`question[${id}]`] : `${mapedQuestions.questions[`question[${id}]`].substring( 0, 60 )}...`,
                    hide: id === props.questionId || operation === 'delete'
                }) );
        });

        const itsChainedScript = computed( () => props.scriptType === 1 );

        return {
            payload,
            showAnswerOptions,
            generateQuestions,
            inputTypes,
            itsChainedScript
        };
    }
};
</script>

<style lang="scss">
    .script-question {
        padding: 20px;
        margin: 30px 20px 0;
        @include element-border( $color-grey-light );
        @include rounded-corner( 22px );
        &__block {
            @include rounded-corner( 19px );
        }
        &__input {
            height: 200px;
            &--disable {
                padding-top: 10px;
            }
        }
    }
</style>